import { useCallback } from "react";
import { usePortfolioBuilderOptional } from "../components/PortfolioBuilderContext";
import type { EditableWatchlist } from "../listTypes";

/**
 * Which list the headline strip under the builder is showing, and a way to
 * change it from a row.
 *
 * A click on a list previews it rather than opening it: seeing the headlines
 * a list catches is the quickest way to judge it, and unlike opening the
 * builder it is a step you can take back by clicking again. It is also what
 * a click does in the widget menus, where it applies the list to the feed.
 *
 * Safe outside the Watchlists page's provider: nothing is ever previewing,
 * and toggling does nothing.
 */
export function useListPreview() {
  const builder = usePortfolioBuilderOptional();
  const state = builder?.previewState;

  const isPreviewing = useCallback(
    (item: EditableWatchlist) => {
      if (!state || state.id === null) return false;
      switch (item.kind) {
        case "watchlist":
          return state.type === "watchlist" && state.id === item.portfolio.id;
        case "tag":
          return state.type === "tagWatchlist" && state.id === item.watchlist.id;
        case "combined":
          return state.type === "filter" && state.id === item.query.id;
      }
    },
    [state]
  );

  /** The setPreview* calls toggle, so a second click clears the preview. */
  const togglePreview = useCallback(
    (item: EditableWatchlist) => {
      if (!builder) return;
      switch (item.kind) {
        case "watchlist":
          builder.setPreviewWatchlist(item.portfolio.id);
          break;
        case "tag":
          builder.setPreviewTagWatchlist(item.watchlist.id, item.watchlist.tags);
          break;
        case "combined":
          if (item.query.id !== undefined) builder.setPreviewFilter(item.query.id);
          break;
      }
    },
    [builder]
  );

  return { isPreviewing, togglePreview };
}
